import { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { motion } from "./fm";
import TiltCard from "./TiltCard";
import LoginForm from "./LoginForm";
import GhostCard from "./GhostCard";

const IDLE_MS = 18000;

export default memo(function CardPortal({ onLogin, axiosInstance, backendStatus, onBackendStatusChange, onShowForgotPassword }) {
  const [awake, setAwake] = useState(false);
  const [hovered, setHovered] = useState(false);
  const [dirty, setDirty] = useState(false);
  const lastActive = useRef(Date.now());

  const bump = useCallback(() => {
    lastActive.current = Date.now();
  }, []);

  useEffect(() => {
    if (!awake) return;
    const id = setInterval(() => {
      if (!dirty && Date.now() - lastActive.current > IDLE_MS) setAwake(false);
    }, 1000);
    return () => clearInterval(id);
  }, [awake, dirty]);

  useEffect(() => {
    if (!awake) return;
    window.addEventListener("keydown", bump);
    return () => window.removeEventListener("keydown", bump);
  }, [awake, bump]);

  const wake = () => {
    if (awake) return;
    bump();
    setAwake(true);
  };

  const wrapperInitial = useMemo(() => ({
    opacity: 0, scale: 0.93, filter: "blur(16px)",
  }), []);
  const wrapperAnimateAwake = useMemo(() => ({
    opacity: 1, scale: 1, filter: "blur(0px)",
  }), []);
  const wrapperAnimateDormant = useMemo(() => ({
    opacity: 0, scale: 0.93, filter: "blur(16px)",
  }), []);
  const wrapperTransitionAwake = useMemo(() => ({
    duration: 0.75, ease: [0.22, 1, 0.36, 1],
  }), []);
  const wrapperTransitionDormant = useMemo(() => ({
    duration: 0.5, ease: [0.22, 1, 0.36, 1],
  }), []);

  return (
    <div
      className="relative w-full max-w-[480px]"
      onPointerMove={awake ? bump : undefined}
      onPointerDown={awake ? bump : undefined}
    >
      <motion.div
        initial={wrapperInitial}
        animate={awake ? wrapperAnimateAwake : wrapperAnimateDormant}
        transition={awake ? wrapperTransitionAwake : wrapperTransitionDormant}
        className={
          "isolation-isolate relative z-20 " +
          (awake ? "" : "pointer-events-none select-none")
        }
        aria-hidden={!awake}
      >
        <TiltCard>
          <LoginForm
            onDirtyChange={setDirty}
            onLogin={onLogin}
            axiosInstance={axiosInstance}
            backendStatus={backendStatus}
            onBackendStatusChange={onBackendStatusChange}
            onShowForgotPassword={onShowForgotPassword}
          />
        </TiltCard>
      </motion.div>

      <div
        role={awake ? undefined : "button"}
        tabIndex={awake ? -1 : 0}
        aria-label={awake ? undefined : "Activate the sign-in card"}
        onClick={awake ? undefined : wake}
        onKeyDown={(e) => {
          if (!awake && (e.key === "Enter" || e.key === " ")) {
            e.preventDefault();
            wake();
          }
        }}
        onPointerEnter={() => {
          setHovered(true);
          if (!awake) wake();
        }}
        onPointerLeave={() => setHovered(false)}
        className={
          awake
            ? "hidden"
            : "z-10 absolute inset-0 cursor-pointer rounded-[28px] outline-none focus-visible:ring-2 focus-visible:ring-sun-400/70"
        }
      >
        <GhostCard dormant={!awake} hovered={hovered} />
      </div>
    </div>
  );
}, function areEqual(prev, next) {
  return (
    prev.onLogin === next.onLogin &&
    prev.axiosInstance === next.axiosInstance &&
    prev.backendStatus?.ok === next.backendStatus?.ok &&
    prev.backendStatus?.message === next.backendStatus?.message &&
    prev.onShowForgotPassword === next.onShowForgotPassword
  );
});
